import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Tag, ExternalLink, Sparkles } from 'lucide-react';
import { supabase } from '../../services/supabase';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  featured_image: string | null;
  author_name: string;
  category: string;
  published_at: string;
  type: 'original';
}

interface CuratedArticle {
  id: string;
  original_url: string;
  title: string;
  excerpt: string | null;
  featured_image: string | null;
  author_name: string | null;
  category: string;
  published_at: string;
  source: {
    name: string;
    slug: string;
    website_url: string;
  };
  type: 'curated';
}

type ContentItem = BlogPost | CuratedArticle;

export function BlogPage() {
  const [originalPosts, setOriginalPosts] = useState<BlogPost[]>([]);
  const [curatedArticles, setCuratedArticles] = useState<CuratedArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'original' | 'curated'>('all');

  useEffect(() => {
    document.title = 'Blog | VZMO - AI Life Coaching';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Explore articles on personal growth, career guidance, mental wellness, and how AI coaching can help you achieve your goals.');
    }
  }, []);

  useEffect(() => {
    async function fetchContent() {
      const [postsResult, curatedResult] = await Promise.all([
        supabase
          .from('blog_posts')
          .select('id, slug, title, excerpt, featured_image, author_name, category, published_at')
          .eq('is_published', true)
          .order('published_at', { ascending: false }),
        supabase
          .from('curated_articles')
          .select(`
            id, original_url, title, excerpt, featured_image, author_name, category, published_at,
            source:content_sources(name, slug, website_url)
          `)
          .eq('is_visible', true)
          .order('published_at', { ascending: false })
          .limit(20)
      ]);

      if (!postsResult.error && postsResult.data) {
        setOriginalPosts(postsResult.data.map((p: Omit<BlogPost, 'type'>) => ({ ...p, type: 'original' as const })));
      }

      if (!curatedResult.error && curatedResult.data) {
        setCuratedArticles(curatedResult.data.map((a: Omit<CuratedArticle, 'type'>) => ({ ...a, type: 'curated' as const })));
      }

      setLoading(false);
    }

    fetchContent();
  }, []);

  const allContent: ContentItem[] = [...originalPosts, ...curatedArticles]
    .sort((a, b) => new Date(b.published_at).getTime() - new Date(a.published_at).getTime());

  const displayContent = activeTab === 'original'
    ? originalPosts
    : activeTab === 'curated'
      ? curatedArticles
      : allContent;

  const allCategories = Array.from(new Set([
    ...originalPosts.map(p => p.category),
    ...curatedArticles.map(a => a.category)
  ]));

  const filteredContent = selectedCategory
    ? displayContent.filter(item => item.category === selectedCategory)
    : displayContent;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (excerpt: string | null) => {
    if (!excerpt) return 3;
    const wordsPerMinute = 200;
    const words = excerpt.split(' ').length * 10;
    return Math.max(3, Math.ceil(words / wordsPerMinute));
  };

  const trackClick = async (articleId: string) => {
    await supabase.rpc('increment_article_click', { article_id: articleId });
  };

  if (loading) {
    return (
      <div className="bg-white pt-20 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 py-24">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white pt-20">
      <section className="py-16 sm:py-24 bg-gradient-to-b from-[#FDF8F4] to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="inline-block text-coral-600 font-medium text-sm tracking-wide uppercase mb-4">
            VZMO Blog
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6 leading-tight">
            Insights for a
            <span className="block text-coral-500">clearer life</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Original articles plus curated wisdom from the best minds in personal growth.
          </p>
        </div>
      </section>

      <section className="py-6 bg-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-full w-fit">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === 'all'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setActiveTab('original')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  activeTab === 'original'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Sparkles className="h-3.5 w-3.5" />
                Original
              </button>
              <button
                onClick={() => setActiveTab('curated')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  activeTab === 'curated'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Curated
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-slate-500">Category:</span>
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  !selectedCategory
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All
              </button>
              {allCategories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors capitalize ${
                    selectedCategory === category
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          {filteredContent.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No articles found.</p>
              <p className="text-slate-400 text-sm mt-2">
                {activeTab === 'curated' && 'Curated articles will appear here once RSS feeds are fetched.'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredContent.map((item, index) => {
                const isOriginal = item.type === 'original';
                const isFeatured = index === 0 && activeTab === 'all';

                if (isOriginal) {
                  const post = item as BlogPost;
                  return (
                    <article
                      key={`original-${post.id}`}
                      className={`group ${isFeatured ? 'md:col-span-2 lg:col-span-3' : ''}`}
                    >
                      <Link to={`/blog/${post.slug}`} className="block">
                        <div className={`bg-[#FDFBF7] rounded-2xl overflow-hidden border border-slate-100 hover:border-coral-200 transition-all hover:shadow-lg ${
                          isFeatured ? 'md:flex md:items-stretch' : ''
                        }`}>
                          {post.featured_image && (
                            <div className={`${isFeatured ? 'md:w-1/2' : ''}`}>
                              <img
                                src={post.featured_image}
                                alt={post.title}
                                className={`w-full object-cover ${isFeatured ? 'h-64 md:h-full' : 'h-48'}`}
                              />
                            </div>
                          )}
                          <div className={`p-6 sm:p-8 ${isFeatured ? 'md:w-1/2 md:flex md:flex-col md:justify-center' : ''}`}>
                            <div className="flex items-center gap-2 mb-4">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-coral-500 text-white text-xs font-medium rounded-full">
                                <Sparkles className="h-3 w-3" />
                                Original
                              </span>
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-coral-100 text-coral-700 text-xs font-medium rounded-full capitalize">
                                <Tag className="h-3 w-3" />
                                {post.category}
                              </span>
                            </div>
                            <h2 className={`font-heading font-bold text-slate-900 mb-3 group-hover:text-coral-600 transition-colors ${
                              isFeatured ? 'text-2xl sm:text-3xl' : 'text-xl'
                            }`}>
                              {post.title}
                            </h2>
                            <p className={`text-slate-600 leading-relaxed mb-4 ${
                              isFeatured ? 'text-base' : 'text-sm line-clamp-3'
                            }`}>
                              {post.excerpt}
                            </p>
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              <span className="flex items-center gap-1.5">
                                <Calendar className="h-4 w-4" />
                                {formatDate(post.published_at)}
                              </span>
                              <span className="flex items-center gap-1.5">
                                <Clock className="h-4 w-4" />
                                {estimateReadTime(post.excerpt)} min read
                              </span>
                            </div>
                            <div className="mt-4 flex items-center text-coral-600 font-medium text-sm group-hover:gap-2 transition-all">
                              Read article
                              <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </Link>
                    </article>
                  );
                } else {
                  const article = item as CuratedArticle;
                  return (
                    <article
                      key={`curated-${article.id}`}
                      className="group"
                    >
                      <a
                        href={article.original_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackClick(article.id)}
                        className="block"
                      >
                        <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-teal-300 transition-all hover:shadow-lg h-full">
                          {article.featured_image && (
                            <div className="relative">
                              <img
                                src={article.featured_image}
                                alt={article.title}
                                className="w-full h-48 object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          <div className="p-6">
                            <div className="flex items-center gap-2 mb-4 flex-wrap">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                                <ExternalLink className="h-3 w-3" />
                                {article.source?.name || 'Curated'}
                              </span>
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full capitalize">
                                {article.category}
                              </span>
                            </div>
                            <h2 className="font-heading font-bold text-slate-900 mb-3 group-hover:text-teal-600 transition-colors text-lg line-clamp-2">
                              {article.title}
                            </h2>
                            {article.excerpt && (
                              <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3">
                                {article.excerpt}
                              </p>
                            )}
                            <div className="flex items-center justify-between text-sm text-slate-500">
                              <span className="flex items-center gap-1.5">
                                <Calendar className="h-4 w-4" />
                                {formatDate(article.published_at)}
                              </span>
                              <span className="flex items-center gap-1 text-teal-600 font-medium group-hover:gap-2 transition-all">
                                Read on {article.source?.name?.split(' ')[0] || 'source'}
                                <ExternalLink className="h-3.5 w-3.5" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </a>
                    </article>
                  );
                }
              })}
            </div>
          )}
        </div>
      </section>

      {curatedArticles.length > 0 && activeTab !== 'curated' && (
        <section className="py-12 bg-slate-50 border-t border-slate-100">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-heading font-bold text-slate-900">
                  From around the web
                </h2>
                <p className="text-slate-500 mt-1">
                  Curated insights from leading personal growth experts
                </p>
              </div>
              <button
                onClick={() => setActiveTab('curated')}
                className="text-teal-600 font-medium text-sm hover:text-teal-700 flex items-center gap-1"
              >
                View all
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              {curatedArticles.slice(0, 4).map(article => (
                <a
                  key={article.id}
                  href={article.original_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick(article.id)}
                  className="group bg-white rounded-xl p-4 border border-slate-200 hover:border-teal-300 transition-all hover:shadow-md"
                >
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded-full mb-3">
                    {article.source?.name || 'Curated'}
                  </span>
                  <h3 className="font-medium text-slate-900 group-hover:text-teal-600 transition-colors line-clamp-2 text-sm">
                    {article.title}
                  </h3>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Ready to try AI coaching?
          </h2>
          <p className="text-xl text-slate-300 mb-10">
            Get personalized guidance for your goals. 7 days free.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center px-10 py-4 bg-coral-500 text-white rounded-full font-semibold text-lg hover:bg-coral-600 transition-all shadow-lg"
          >
            Start free trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
